/*
  File: src/pages/ManagementHub.jsx
  Purpose: Central hub for all management and testing tools
  Features:
  - Consolidated navigation for all management pages
  - Quick access to testing tools
  - Admin-focused layout with organized sections
  - Respects REACT_APP_SHOW_DEV_PAGES environment variable
*/
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';

function ManagementHub() {
    // Dev-only pages visibility flag: enable via REACT_APP_SHOW_DEV_PAGES=true
    // Defaults to true in non-production builds
    const SHOW_DEV_PAGES = useMemo(() => {
        const envVal = String(process.env.REACT_APP_SHOW_DEV_PAGES || '').trim().toLowerCase();
        if (envVal === 'true') return true;
        if (envVal === 'false') return false;
        return process.env.NODE_ENV !== 'production';
    }, []);
    const managementLinks = [
        {
            title: 'Product Management',
            description: 'Manage products, pricing, and inventory',
            path: '/productmanagement',
            icon: '📦',
            category: 'Store Management'
        },
        {
            title: 'Product Update',
            description: 'Update existing products and manage variants',
            path: '/productupdate',
            icon: '✏️',
            category: 'Store Management'
        },
        {
            title: 'Characters Management',
            description: 'Manage characters and their properties',
            path: '/characters',
            icon: '👤',
            category: 'Content Management'
        },
        {
            title: 'Players Management',
            description: 'Manage players and their profiles',
            path: '/players',
            icon: '⚽',
            category: 'Content Management'
        },
        {
            title: 'Vendors Management',
            description: 'Manage vendors and suppliers',
            path: '/vendors',
            icon: '🏪',
            category: 'Store Management'
        },
        {
            title: 'Categories Management',
            description: 'Manage product categories and organization',
            path: '/categories',
            icon: '📂',
            category: 'Store Management'
        },
        {
            title: 'Rekognition Management',
            description: 'Manage datasets, images, and annotations',
            path: '/rekognition-management',
            icon: '🤖',
            category: 'AI/ML Tools'
        },
        {
            title: 'Rekognition Test',
            description: 'Test and debug AI recognition models',
            path: '/rekognition-test',
            icon: '🔬',
            category: 'Testing Tools',
            devOnly: true
        },
        {
            title: 'Snaps',
            description: 'View and manage snap captures',
            path: '/snaps',
            icon: '📸',
            category: 'Testing Tools',
            devOnly: true
        },
        {
            title: 'Product Image Resizing',
            description: 'Convert and optimize product images to WebP format',
            path: '/product-resizing',
            icon: '🖼️',
            category: 'Store Management'
        }
    ];

    // Filter links based on dev pages setting
    const filteredLinks = managementLinks.filter(link => {
        // Show all non-dev links
        if (!link.devOnly) return true;
        // Show dev links only if SHOW_DEV_PAGES is true
        return SHOW_DEV_PAGES;
    });

    // Group links by category
    const groupedLinks = filteredLinks.reduce((acc, link) => {
        if (!acc[link.category]) {
            acc[link.category] = [];
        }
        acc[link.category].push(link);
        return acc;
    }, {});

    const categories = Object.keys(groupedLinks);

    // If no management pages are available, show a message
    if (filteredLinks.length === 0) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#0a0e1a] via-[#1a1f2e] to-[#2a2f3e]">
                <Navbar />
                <main className="pt-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                        <div className="text-center">
                            <h1 className="text-4xl font-bold text-white mb-4">
                                Management & Testing Hub
                            </h1>
                            <p className="text-white/60 text-lg">
                                No management tools are currently available.
                            </p>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#0a0e1a] via-[#1a1f2e] to-[#2a2f3e]">
            <Navbar />
            <main className="pt-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <h1 className="text-4xl font-bold text-white mb-4">
                            Management & Testing Hub
                        </h1>
                        <p className="text-white/60 text-lg max-w-2xl mx-auto">
                            Centralized access to all management tools and testing utilities. 
                            Choose from the sections below to manage your platform.
                        </p>
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                        {groupedLinks['Store Management'] && (
                            <div className="bg-gradient-to-br from-[#1a2448]/80 to-[#1a2448]/60 rounded-xl p-6 border border-[#4cc3e9]/30">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                                        <span className="text-2xl">🏪</span>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-white">Store Management</h3>
                                        <p className="text-white/60 text-sm">Products, vendors, categories</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        {groupedLinks['Content Management'] && (
                            <div className="bg-gradient-to-br from-[#1a2448]/80 to-[#1a2448]/60 rounded-xl p-6 border border-[#4cc3e9]/30">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                                        <span className="text-2xl">📝</span>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-white">Content Management</h3>
                                        <p className="text-white/60 text-sm">Characters, players, content</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        {(groupedLinks['AI/ML Tools'] || groupedLinks['Testing Tools']) && (
                            <div className="bg-gradient-to-br from-[#1a2448]/80 to-[#1a2448]/60 rounded-xl p-6 border border-[#4cc3e9]/30">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                                        <span className="text-2xl">🤖</span>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-white">AI & Testing</h3>
                                        <p className="text-white/60 text-sm">ML models, testing tools</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Management Sections */}
                    <div className="space-y-12">
                        {categories.map((category) => (
                            <div key={category}>
                                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                                    <div className="w-1 h-8 bg-gradient-to-b from-[#7b3ff2] to-[#9060ff] rounded"></div>
                                    {category}
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {groupedLinks[category].map((link) => (
                                        <Link
                                            key={link.path}
                                            to={link.path}
                                            className="group bg-gradient-to-br from-[#1a2448]/80 to-[#1a2448]/60 rounded-xl p-6 border border-[#4cc3e9]/30 hover:border-[#4cc3e9]/50 transition-all duration-200 hover:scale-105"
                                        >
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-2xl group-hover:bg-white/20 transition-colors">
                                                    {link.icon}
                                                </div>
                                                <svg 
                                                    className="w-5 h-5 text-white/40 group-hover:text-white/60 transition-colors" 
                                                    fill="none" 
                                                    stroke="currentColor" 
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </div>
                                            <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#4cc3e9] transition-colors">
                                                {link.title}
                                            </h3>
                                            <p className="text-white/60 text-sm leading-relaxed">
                                                {link.description}
                                            </p>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Footer */}
                    <div className="mt-16 pt-8 border-t border-white/10">
                        <div className="text-center">
                            <p className="text-white/40 text-sm">
                                Need help? Contact support or check the documentation for each management tool.
                            </p>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}

export default ManagementHub;